import React from 'react';
import { Eye, FileText, Stethoscope } from 'lucide-react';
import { Badge } from '../ui/badge';

/**
 * Live Preview - Shows real-time preview of letter as user types (Patient mode)
 */
export const LivePreview = ({
  notes,
  soldierRank,
  soldierFirstName,
  soldierLastName,
  soldierDob,
  soldierUnit,
  letterType,
  occupation,
  jobDuties
}) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Eye className="w-4 h-4" />
        <span>Live Preview</span>
        <Badge variant="outline" className="text-xs">Updates as you type</Badge>
      </div>
      <div className="p-4 bg-secondary/50 border border-border rounded-lg min-h-[300px] font-serif text-sm leading-relaxed">
        {/* Letter Preview */}
        <div className="space-y-4 text-foreground">
          <div className="text-center border-b border-border/50 pb-3 mb-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Preview - Your letter will look similar to this</p>
          </div>
          
          {/* Header */}
          <div className="text-right text-xs text-muted-foreground">
            {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
          
          {/* Patient Info Block */}
          <div className="space-y-1">
            <p><strong>{soldierRank ? `${soldierRank} ` : ''}{soldierFirstName || '[Your First Name]'} {soldierLastName || '[Your Last Name]'}</strong></p>
            {soldierUnit && <p className="text-muted-foreground">{soldierUnit}</p>}
            {soldierDob && <p className="text-muted-foreground text-xs">DOB: {soldierDob}</p>}
          </div>
          
          {/* Subject Line */}
          {letterType && (
            <div className="mt-4 pt-2 border-t border-border/30">
              <p><strong>RE: </strong>{letterType === 'custom' ? 'Administrative Letter' : letterType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} for {soldierFirstName || '[Name]'} {soldierLastName}</p>
            </div>
          )}
          
          {/* Body Preview */}
          <div className="mt-4 space-y-3 text-muted-foreground">
            {notes && notes.trim() ? (
              <>
                <p className="italic text-xs">[Based on your description, your letter will include:]</p>
                <p className="pl-4 border-l-2 border-primary/30">{notes.length > 200 ? notes.substring(0, 200) + '...' : notes}</p>
              </>
            ) : (
              <p className="italic text-xs">[Start describing your situation to see a preview]</p>
            )}
            
            {/* Job Duties Preview */}
            {(occupation || jobDuties) && (
              <div className="mt-3 pt-2 border-t border-border/30">
                <p className="italic text-xs">[Your job context will be included:]</p>
                {occupation && <p className="pl-4">Position: {occupation}</p>}
                {jobDuties && <p className="pl-4 text-xs">Duties affected: {jobDuties.length > 100 ? jobDuties.substring(0, 100) + '...' : jobDuties}</p>}
              </div>
            )}
          </div>
          
          {/* Signature Placeholder */}
          <div className="mt-6 pt-4 border-t border-border/30 text-xs text-muted-foreground">
            <p className="italic">[Provider signature block will appear here after your provider reviews]</p>
          </div>
        </div>
      </div>
      <p className="text-xs text-muted-foreground text-center">
        Click "Generate Letter Draft" to create your full letter with proper formatting
      </p>
    </div>
  );
};

/**
 * Empty State - Shown when no content yet
 */
export const EmptyState = ({ effectiveRole }) => {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-14 h-14 bg-secondary rounded-full flex items-center justify-center mb-3">
        {effectiveRole === 'patient' ? <FileText className="w-7 h-7 text-muted-foreground" /> : <Stethoscope className="w-7 h-7 text-muted-foreground" />}
      </div>
      <p className="text-muted-foreground text-sm">{effectiveRole === 'patient' ? 'Describe your situation and select a letter type' : 'Enter your notes and click Save & Create Documents'}</p>
      <p className="text-muted-foreground/70 text-xs mt-1">{effectiveRole === 'patient' ? 'Your draft letter will appear here' : 'Referrals, clinical notes, and letters will appear here'}</p>
    </div>
  );
};

export default LivePreview;
